import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import _groupBy from 'lodash/groupBy';
import _camelCase from 'lodash/camelCase';
import { connect } from 'react-redux';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { Button, Row, Form } from 'react-bootstrap';
import ReactTooltip from 'react-tooltip';
// Services
import { updateNextActionDate, getNodPatientsByDate } from '../../../services/patient';
// Actions
import ShowNotification from '../../../actions/notification';
import { UpdatePatient } from '../../../actions/patient';
// Constants
import {
  USER_ROLES, PATIENT_CALL_TIME_OPTIONS, DATE_FORMAT,
  NOTIFICATION_TYPE, DIALOG_STYLES, NEXT_OUTREACH_DATE_BLANK,
  PATIENT_CHANGE_NOD_REASON_OPTIONS,
} from '../../../constants/constants';
// Components
import { Select } from '../../base/forms/Select';
import { Checkbox } from '../../base/forms/Checkbox';
import { Datepicker } from '../../base/forms/Datepicker';
import { TextArea } from '../../base/forms/TextArea';

const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
// This is the maximum year for java.sql.Timestamp
const maxValidDate = moment('12/31/9999', DATE_FORMAT.FULL_WITH_LOWERCASE);

const NextOutreachDateForm = ({
  initialNextOutreachDate,
  handleCallTimeOptions,
  handleReasonOptions,
  engoodenTextConsent,
  isCnOrPes,
  userId,
  showNotification,
  patientName,
  handleCloseModal,
  clearNextOutreachDate
}) => {
  const { values, setFieldValue, setFieldTouched, validateForm, handleSubmit, isValid } = useFormikContext();

  const prevDateRef = React.useRef(values.date ? moment(values.date).format(DATE_FORMAT.FULL_SERVER) : null);

  useEffect(() => {
    const currentFormatted = values.date ? moment(values.date).format(DATE_FORMAT.FULL_SERVER) : null;

    if (initialNextOutreachDate && currentFormatted !== prevDateRef.current) {
      prevDateRef.current = currentFormatted;
      
      const systemDate = initialNextOutreachDate.systemReminderDate ? moment(initialNextOutreachDate.systemReminderDate) : null;
      const initialDate = initialNextOutreachDate.reminderDate ? moment(initialNextOutreachDate.reminderDate) : null;
      const currentDate = values.date ? moment(values.date) : null;

      if (!currentDate) return;

      const isSystemDate = systemDate && currentDate.isSame(systemDate, 'day');
      const isInitialSavedDate = initialDate && currentDate.isSame(initialDate, 'day');
      
      // Pass 'false' as the 3rd argument to prevent overlapping validation runs!
      if (isSystemDate) {
        setFieldValue('reason', '', false);
        setFieldValue('otherText', '', false);
        setFieldTouched('reason', false, false); 
      } else if (isInitialSavedDate) {
        setFieldValue('reason', initialNextOutreachDate.reason || '', false);
        setFieldValue('otherText', initialNextOutreachDate.otherText || '', false);
        setFieldTouched('reason', false, false); 
      } else {
        setFieldValue('reason', '', false);
        setFieldValue('otherText', '', false);
        setFieldTouched('reason', true, false); // Touch the field to ensure the UI error displays
      }

      // Force a single, clean validation pass immediately after the state settles
      setTimeout(() => {
        validateForm();
      }, 0);
    }
  }, [values.date, initialNextOutreachDate, setFieldValue, setFieldTouched, validateForm]);

  return (
    <Form>
      <div className="text-left dialog-content">
        <Row className="align-items-center mb-3">
          <div className="col">
            <Datepicker
              name="date"
              showYearDropdown
              minDate={new Date()}
              maxDate={maxValidDate.toDate()}
              showDaysAlert
              inline
              todayButton="Today"
              highlight={initialNextOutreachDate?.systemReminderDate ? moment(initialNextOutreachDate.systemReminderDate).toDate() : null}
            />
          </div>
          <div className="col" data-test="nextOutreachDateModal">
            <Datepicker
              label="Date"
              name="date"
              dateFormat={DATE_FORMAT.FULL_WITH_LOWERCASE}
              popperClassName="d-none"
              data-test="nextOutreachDateModal_dateInput"
            />
            <Select
              label="Time"
              name="preferredCallTime"
              options={handleCallTimeOptions()}
              data-test="nextOutreachDateModal_timeInput"
            />
            {/* Show Reason Select if the date differs from the system date */}
            {initialNextOutreachDate?.systemReminderDate && !moment(values.date).isSame(moment(initialNextOutreachDate.systemReminderDate), 'day') && (
              <Select
                label="Reason"
                name="reason"
                options={handleReasonOptions()}
                data-test="nextOutreachDateModal_reasonInput"
              />
            )}
            {/* Show Text Area only if Reason is 'Other' */}
            {values.reason === 'Other' && (
              <React.Fragment>
                <TextArea
                  name="otherText"
                  maxLength={250}
                  data-test="nextOutreachDateModal_reasonOtherText"
                />
                <div 
                  className="text-ccm-gray text-right font-italic" 
                  style={{ fontSize: '12px', marginTop: '-12px', marginBottom: '12px' }}
                >
                  {250 - (values.otherText || '').length} characters remaining
                </div>
              </React.Fragment>
            )}
            <Form.Label>Type</Form.Label>
            <Checkbox
              id="outreachType_call"
              label={<span className="bi-telephone-fill">&nbsp;Monthly Outreach Call</span>}
              name="contactMethod"
              value="phone"
              type="radio"
              data-test="nextOutreachDateModal_callInput"
            />
            {(engoodenTextConsent && engoodenTextConsent === 'ALLTEXTS') && (
            <Checkbox
              id="outreachType_text"
              label={<span className="bi-chat-right-dots" data-test="nextOutreachDateModal_textInput">&nbsp;Text Check-in</span>}
              name="contactMethod"
              value="text"
              type="radio"
            />)}
          </div>
        </Row>
        {isCnOrPes && (
        <NodPatientsTable
          maxValidDate={maxValidDate}
          userId={userId}
          showNotification={showNotification}
          patientName={patientName}
          data-test="nextOutreachDateModal_callSchedule"
        />
        )}
      </div>
      <div className="dialog-buttons justify-content-end px-4">
        <Button variant="light" onClick={() => handleCloseModal()} data-test="nextOutreachDateModal_cancelBtn">Cancel</Button>
        <Button
          variant="light"
          className="ml-2"
          onClick={() => clearNextOutreachDate()}
          data-test="nextOutreachDateModal_clearBtn"
          data-for="tooltip-nextOutreachDate"
          data-tip="Remove this Next Outreach Date"
        >
          Clear Next Outreach Date
        </Button>
        <Button
          variant="primary"
          className="ml-2"
          onClick={() => handleSubmit()}
          disabled={!isValid}
          data-test="nextOutreachDateModal_saveBtn"
        >
          Save
        </Button>
      </div>
    </Form>
  );
};

export function NextOutreachDateModal(props) {
  const {
    isModalOpen, setIsModalOpen, initialNextOutreachDate,
    user: { id: userId, role: userRole }, showNotification,
    patient: {
      firstName, lastName,
      profile: {
        preferredCallTime,
        textMessageConsent: engoodenTextConsent,
      } = {},
    } = {}, nextOutreachElementSave,
  } = props;

  const isCnOrPes = userRole === USER_ROLES.CN || userRole === USER_ROLES.PES;
  const profilePreferredCallTime = (preferredCallTime === undefined || preferredCallTime === 'NONE')
    ? 'ANYTIME' : preferredCallTime;

  const DEFAULT_NEXT_OUTREACH_DATE = {
    id: '',
    date: moment(),
    preferredCallTime: profilePreferredCallTime,
    contactMethod: 'phone',
    reason: '',
    otherText: '',
  };

  const [newOutreachDate, setNewOutreachDate] = useState(DEFAULT_NEXT_OUTREACH_DATE);

  const getPreferredCallTime = (initialValues) => {
    if (initialValues.reminderTime === null) {
      return 'NONE';
    }
    return initialValues.reminderTime
      && initialValues.reminderDate
      ? initialValues.reminderTime : profilePreferredCallTime;
  };

  useEffect(() => {
    if (initialNextOutreachDate) {
      setNewOutreachDate({
        id: initialNextOutreachDate.id || '',
        date: (initialNextOutreachDate.reminderDate
          && moment(initialNextOutreachDate.reminderDate).isValid())
          ? moment(initialNextOutreachDate.reminderDate)
          : ((initialNextOutreachDate.systemReminderDate
            && !moment(initialNextOutreachDate.systemReminderDate).isSame(moment()))
            ? moment(initialNextOutreachDate.systemReminderDate) : moment()),
        preferredCallTime: getPreferredCallTime(initialNextOutreachDate),
        contactMethod: initialNextOutreachDate.contactMethod || 'phone',
        reason: initialNextOutreachDate.reason || '',
        otherText: initialNextOutreachDate.otherText || '',
      });
    } else {
      setNewOutreachDate(DEFAULT_NEXT_OUTREACH_DATE);
    }
  }, [initialNextOutreachDate, preferredCallTime]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const saveNextOutreachDate = (updatedNextOutreachInfo) => {
    const { updatePatient, patientId, loading } = props;

    if (loading) return {};

    const updateNextOutreachDateRequest = updateNextActionDate(patientId, updatedNextOutreachInfo);
    const updateNextOutreachDatePromise = updateNextOutreachDateRequest.promise;

    return updateNextOutreachDatePromise.then(() => {
      delete updateNextOutreachDatePromise.promise;
      const reminderDateUpdated = updatedNextOutreachInfo && updatedNextOutreachInfo.date
        ? moment(updatedNextOutreachInfo.date, DATE_FORMAT.FULL)
          .format(DATE_FORMAT.FULL_SERVER) : null;
      updatePatient({
        nextActionDateReminderInfo: {
          ...initialNextOutreachDate,
          id: updatedNextOutreachInfo.id,
          reminderDate: reminderDateUpdated,
          contactMethod: updatedNextOutreachInfo.contactMethod,
          reminderTime: updatedNextOutreachInfo.preferredCallTime,
          reason: updatedNextOutreachInfo.reason,
          otherText: updatedNextOutreachInfo.otherText,
        },
      });
      if (nextOutreachElementSave) {
        nextOutreachElementSave({
          ...initialNextOutreachDate,
          id: updatedNextOutreachInfo.id,
          reminderDate: reminderDateUpdated,
          contactMethod: updatedNextOutreachInfo.contactMethod,
          reminderTime: updatedNextOutreachInfo.preferredCallTime,
          reason: updatedNextOutreachInfo.reason,
          otherText: updatedNextOutreachInfo.otherText,
        });
      }
      showNotification({
        message: 'Next outreach info saved.',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.SUCCESS,
      });
      handleCloseModal();
    }).catch((error) => {
      delete updateNextOutreachDatePromise.promise;

      if (error.isCanceled || error.status === 401 || error.status === 403) return;

      showNotification({
        message: 'Could not update next outreach info.',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.ERROR,
      });
    });
  };

  const handleCallTimeOptions = () => {
    const callTimeOptions = [];
    PATIENT_CALL_TIME_OPTIONS.forEach((opt) => {
      callTimeOptions.push({
        value: opt.value,
        label: `${opt.name} ${preferredCallTime === profilePreferredCallTime && profilePreferredCallTime === opt.value ? '(Preferred)' : ''}`,
      });
    });
    return callTimeOptions;
  };

  const handleReasonOptions = () => {
    const reasonOptions = [];
    PATIENT_CHANGE_NOD_REASON_OPTIONS.forEach((opt) => {
      reasonOptions.push({
        value: opt.value,
        label: opt.name,
      });
    });
    return reasonOptions;
  };

  const clearNextOutreachDate = async () => {
    saveNextOutreachDate(NEXT_OUTREACH_DATE_BLANK);
    handleCloseModal();
  };

  return (
    <Modal
      isOpen={isModalOpen}
      style={DIALOG_STYLES}
      onRequestClose={() => handleCloseModal()}
      contentLabel="Next Outreach Date Modal"
      data-test="nextOutreachDateModal_onRequestClose"
    >
      <div className="simple-dialog medium-dialog nextOutreachDate-modal">
        <div className="dialog-title">
          {`${initialNextOutreachDate ? 'Edit' : 'Add'} Next Outreach Date`}
          <button
            type="button"
            className="close-icon i-close"
            onClick={() => handleCloseModal()}
            data-test="nextOutreachDateModal_closeBtn"
          />
        </div>
        <Formik
          enableReinitialize
          initialValues={newOutreachDate}
          enableReinitialize
          validationSchema={Yup.object({
            date: Yup.date()
              .typeError('Must be a valid date')
              .min(moment(todayDate).format(DATE_FORMAT.SHORT), 'Must not be in the past')
              .max(maxValidDate, 'The year of the date should not exceed 4 digits')
              .required('Required'),
            preferredCallTime: Yup.string().typeError('Please select a time frame').required('Required'),
            contactMethod: Yup.string().oneOf(['phone', 'text']).required('Required'),
            reason: Yup.string().nullable().when('date', {
              is: (date) => {
                const systemDate = initialNextOutreachDate?.systemReminderDate;
                return !!(systemDate && date && !moment(date).isSame(moment(systemDate), 'day'));
              },
              then: Yup.string().nullable().required('Must select a reason.'),
              otherwise: Yup.string().nullable().notRequired(),
            }),
            otherText: Yup.string().max(250, 'Must be 250 characters or less'),
          })}
          onSubmit={(values, { resetForm }) => {
            const formattedDate = values.date
              ? moment(values.date, DATE_FORMAT.FULL).format(DATE_FORMAT.FULL) : null;
            const updatedValues = { ...values, date: formattedDate };
            saveNextOutreachDate(updatedValues);
            resetForm();
          }}
          data-test="nextOutreachDateModal_formikComponent"
        >
          {formik => (
            <NextOutreachDateForm
              initialNextOutreachDate={initialNextOutreachDate}
              handleCallTimeOptions={handleCallTimeOptions}
              handleReasonOptions={handleReasonOptions}
              engoodenTextConsent={engoodenTextConsent}
              isCnOrPes={isCnOrPes}
              userId={userId}
              showNotification={showNotification}
              patientName={`${firstName} ${lastName}`}
              handleCloseModal={handleCloseModal}
              clearNextOutreachDate={clearNextOutreachDate}
            />
          )}
        </Formik>
      </div>
      <ReactTooltip id="tooltip-nextOutreachDate" type="info" effect="solid" place="top" />
    </Modal>
  );
}

export const NodPatientsTable = (props) => {
  const { values: formikValues } = useFormikContext();
  const {
    userId, showNotification, patientName, maxValidDate,
  } = props;

  const [nodPatients, setNodPatients] = useState({});

  const loadNodPatients = (selectedDate) => {
    const getNodPatientsRequest = getNodPatientsByDate(userId, selectedDate);
    const getNodPatientsPromise = getNodPatientsRequest.promise;

    return getNodPatientsPromise.then((data) => {
      setNodPatients(data);
    }).catch((error) => {
      delete getNodPatientsRequest.promise;

      if (error.isCanceled || error.status === 401 || error.status === 403) {
        return;
      }

      showNotification({
        message: 'Could not load Next Outreach Date patients, please try again later',
        autoHide: true,
        notificationType: NOTIFICATION_TYPE.ERROR,
      });
    });
  };

  // 1. Extract a flat string value so React can safely compare it between renders
  const selectedDateString = formikValues?.date 
    ? moment(formikValues.date, DATE_FORMAT.FULL).format(DATE_FORMAT.FULL_SERVER) 
    : null;

  useEffect(() => {
    if (selectedDateString) {
      const selectedDate = moment(selectedDateString, DATE_FORMAT.FULL_SERVER);
      const difference = selectedDate.diff(maxValidDate, 'years');

      if (difference < 0) {
        // 2. Safely call the API ONLY when the dateString actually changes
        loadNodPatients(selectedDateString);
      }
    }
  // 3. Depend on the string, NOT the formikValues object
  }, [selectedDateString, maxValidDate, userId]);

  const totalScheduled = Object.values(nodPatients).reduce((total, patientsArray) => total + patientsArray.length, 0);

  const renderEmptyRow = () => (
    <p className="call-schedule__empty" data-test="nextOutreachDateModal_emptyCallSchedule">
      No scheduled patients on this day
    </p>);

  const renderPatientsRows = (patients) => {
    const groupedNodPatientsByTime = _groupBy(patients, el => el.time);

    const subcategoryOrder = [
      'Early morning', 'Morning', 'Late morning',
      'Early afternoon', 'Afternoon', 'Late afternoon',
      'Anytime', 'None Specified'
    ];
    const statusOrder = ['CN', 'CC', 'CS'];

    const sortEntriesByOrder = (groupedObj, orderArray) => {
      const getOrder = (item) => orderArray.includes(item) ? orderArray.indexOf(item) : orderArray.length;
      return Object.entries(groupedObj).sort(([keyA], [keyB]) => (
        (getOrder(keyA) - getOrder(keyB)) || keyA.localeCompare(keyB)
      ));
    };

    const sortedSubcategories = sortEntriesByOrder(groupedNodPatientsByTime, subcategoryOrder);

    return sortedSubcategories.map(([subcategory, rows]) => {
      const groupedByStatus = _groupBy(rows, el => el.status);
      const sortedStatuses = sortEntriesByOrder(groupedByStatus, statusOrder);

      return (
        <div
          className="p-2"
          key={`callSchedule_${_camelCase(subcategory)}`}
          data-test={`nextOutreachDateModal_callSchedule${_camelCase(subcategory)}`}
        >
          <p className="call-schedule__subcategory-name mb-1 font-weight-bold">{subcategory}</p>
          {sortedStatuses.map(([status, statusRows]) => (
            <p
              key={`callSchedule_${_camelCase(subcategory)}_${status}`}
              className="call-schedule__patient-name ml-2 mb-1"
              data-test={`nextOutreachDateModal_callScheduleStatus_${status}`}
            >
              {status} - {statusRows.length}
            </p>
          ))}
        </div>
      );
    });
  };

  const renderNodPatientsTable = () => {
    const getTimeCategory = (time) => {
      switch (time) {
        case 'Morning':
        case 'Late morning':
        case 'Early morning':
          return 'Morning';
        case 'Afternoon':
        case 'Late afternoon':
        case 'Early afternoon':
          return 'Afternoon';
        default:
          return 'Anytime';
      }
    };
    const nodPatientsWithTime = [];

    Object.entries(nodPatients).forEach(([time, patients]) => {
      patients.forEach(patient => {
        nodPatientsWithTime.push({
          status: patient.status,
          category: getTimeCategory(time),
          time,
        });
      });
    });

    if (nodPatientsWithTime.length > 0) {
      const groupedNodPatientsByCat = _groupBy(nodPatientsWithTime, el => el.category);
      return Object.entries(groupedNodPatientsByCat).map(([category, patients]) => (
        <div
          key={`callSchedule_${category}`}
          className="call-schedule-category col-4"
          data-test={`nextOutreachDateModal_callSchedule${category}`}
        >
          <div className="call-schedule__category-name bg-ccm-blue px-2 py-1 d-flex justify-content-between">
            <span>{category}</span>
            <span>{patients.length}</span>
          </div>
          {renderPatientsRows(patients)}
        </div>
      ));
    }
    return renderEmptyRow();
  };

  return (
    <Row className="no-gutters call-schedule">
      <div className="call-schedule-heading d-flex-center mb-3">
        <i className="bi-calendar-check mr-2" />
        <span className="mb-0 mr-2" data-test="nextOutreachDateModal_callSchedule">Call Schedule</span>
        {formikValues && formikValues.date && (
        <small className="text-ccm-gray" data-test="nextOutreachDateModal_selectedDate">
          {moment(formikValues.date, DATE_FORMAT.FULL).format(DATE_FORMAT.FULL_WITH_DAY)}
        </small>)}
        <span className="mb-0 mr-2 px-2 medium-text font-weight-normal" data-test="nextOutreachDateModal_totalScheduled">
          Total Scheduled - {totalScheduled}
        </span>
      </div>
      <Row data-test="nextOutreachDateModal_callScheduleBody" className="call-schedule-content overflow-auto no-gutters w-100">
        {renderNodPatientsTable()}
      </Row>
    </Row>);
};

function mapStateToProps(state) {
  return {
    user: state.user,
    patient: state.patient,
    loading: state.requestsInProgress.count,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    updatePatient: patientData => dispatch(UpdatePatient(patientData)),
    showNotification: notificationData => dispatch(ShowNotification(notificationData)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NextOutreachDateModal);
